// @ts-nocheck
/*
 * Port of views/dashboard/module/edit_acl.js (origin/main-dev, 1257 lines).
 * BYTE-IDENTICAL — GET pre-populates aclName + selectedModules from
 * module_operations, redirects non-superadmin users away from default
 * ACLs (line 305-313), no parent-ACL checkbox (edit-mode), no defaultAcl
 * role disable-list (edit-mode). All 9 modals + normalize/save handlers
 * preserved. Chrome swap: v1 <Card> → shadcn.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card as ShadcnCard } from '@/components/ui/card';
import {
  API_MODULE_LIST,
  API_ACL_SETTING_EDIT,
  API_ACL_SETTING_LIST,
} from '@/service/url';
import { postApiCall, getApiCall } from '@/service/httpCall';
import { FreezePasswordPrompt, isAclLocked } from './aclLockUi';
import { useNavigate, useParams } from 'react-router-dom';
import { getUser } from '@/utilities/setting';
import { USER_ROLES, ROUTES, getAclModuleDisplayLabel, isAclOpSupported } from '@/utilities/constant';
import AgentLeadsCustomAclModal from '@/features/acl-settings/AgentLeadsCustomAclModal';
import {
  AGENT_LEADS_ACL_MODULE,
  CUSTOM_LEADS_ACL_OPTION,
  canGrantAgentLeadsAcl,
  formatAgentLeadsCustomAclLabel,
  getParentGrantableCustomLeads,
  isAgentLeadsCustomRelevant,
  normalizeAgentLeadsCustomAclForSave,
} from '@/utilities/agentLeadsQuota';
import {
  VOICE_PLAN_ACL_MODULE,
  isVoicePlanAclActive,
  normalizeVoicePlanDirectionsForSave,
  formatPlanDirectionsLabel,
  parseModuleOperations,
  getVoicePlanDirectionOptionsForEditor,
  getParentGrantablePlanDirections,
} from '@/utilities/voicePlanAcl';
import VoicePlanDirectionsModal from '@/features/acl-settings/VoicePlanDirectionsModal';
import VoiceLibraryAclModal from '@/features/acl-settings/VoiceLibraryAclModal';
import {
  VOICE_LIBRARY_ACL_MODULE,
  isVoiceLibraryAclActive,
  normalizeVoiceLibraryAclForSave,
  formatVoiceLibraryAclLabel,
  getVoiceTypeOptionsForEditor,
  getLanguageOptionsForEditor,
  getParentGrantableVoiceLibraryRestrictions,
} from '@/utilities/voiceLibraryAcl';
import CallGroupEditAclModal from '@/features/acl-settings/CallGroupEditAclModal';
import AgentEditAclModal from '@/features/acl-settings/AgentEditAclModal';
import {
  CALL_GROUP_ACL_MODULE,
  normalizeCallGroupEditOptionsForSave,
  DEFAULT_CALL_GROUP_EDIT_OPTIONS,
  formatCallGroupEditOptionsLabel,
  getCallGroupEditOptionsForEditor,
  getParentGrantableCallGroupEditOptions,
} from '@/utilities/callGroupAcl';
import {
  AGENT_ACL_MODULE,
  normalizeAgentEditOptionsForSave,
  DEFAULT_AGENT_EDIT_OPTIONS,
  formatAgentEditOptionsLabel,
  getAgentEditOptionsForEditor,
  getParentGrantableAgentEditOptions,
} from '@/utilities/agentAcl';
import IvrEditAclModal from '@/features/acl-settings/IvrEditAclModal';
import {
  IVR_ACL_MODULE,
  normalizeIvrNodeOptionsForSave,
  DEFAULT_IVR_NODE_OPTIONS,
  formatIvrNodeOptionsLabel,
  getIvrNodeOptionsForEditor,
  getParentGrantableIvrNodeOptions,
} from '@/utilities/ivrAcl';
import CallCentreVoicemailAclModal from '@/features/acl-settings/CallCentreVoicemailAclModal';
import CompanyAssetsVisibilityAclModal from '@/features/acl-settings/CompanyAssetsVisibilityAclModal';
import { useAssetsVisibilityEligibility } from '@/hooks/useAssetsVisibilityEligibility';
import {
  COMPANY_ACL_MODULE,
  normalizeCompanyAssetsVisibilityAclForSave,
  formatCompanyAssetsVisibilityAclLabel,
} from '@/utilities/companyAssetsVisibilityAcl';
import UserEmailVerificationAclModal from '@/features/acl-settings/UserEmailVerificationAclModal';
import {
  CALL_CENTRE_ACL_MODULE,
  normalizeCallCentreVoicemailAclForSave,
  formatCallCentreVoicemailAclLabel,
  getParentGrantableCallCentreVoicemail,
} from '@/utilities/callCentreVoicemailAcl';
import {
  normalizeCallCentreTranscriptionAclForSave,
  formatCallCentreTranscriptionAclLabel,
  getParentGrantableCallCentreTranscription,
} from '@/utilities/callCentreTranscriptionAcl';
import {
  USERS_ACL_MODULE,
  normalizeUsersEmailVerificationAclForSave,
  formatUsersEmailVerificationAclLabel,
  getParentGrantableUsersEmailVerification,
} from '@/utilities/userEmailVerificationAcl';
import DispositionFormsTimingAclModal from '@/features/acl-settings/DispositionFormsTimingAclModal';
import {
  DISPOSITION_FORMS_ACL_MODULE,
  normalizeDispositionTimingOptionsForSave,
  DEFAULT_DISPOSITION_TIMING_OPTIONS,
  formatDispositionTimingAclLabel,
  getParentGrantableDispositionTimingOptions,
  CUSTOM_TIMING_ACL_OPTION,
  ALLOW_FOLLOWUPS_ACL_OPTION,
} from '@/utilities/dispositionFormsAcl';
import TtsOperatorAclModal from '@/features/acl-settings/TtsOperatorAclModal';
import {
  TTS_ACL_MODULE,
  formatTtsOperatorLabel,
  normalizeTtsOperatorForSave,
} from '@/utilities/ttsAcl';
import AiAgentDescLimitAclModal from '@/features/acl-settings/AiAgentDescLimitAclModal';
import {
  AI_AGENT_ACL_MODULE,
  formatAiAgentDescLimitAclLabel,
  normalizeAiAgentDescLimitForSave,
} from '@/utilities/aiAgentAcl';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import {
  AclSettingsPageHeader,
  AclSettingsFormTop,
  AclModulePermissionsToolbar,
  AclModulePermissionsTable,
  renderAclOptionsCell,
} from '@/features/acl-settings/aclSettingsUi';
import '@/features/acl-settings/acl-settings.scss';

import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Storage } from '@/utilities/setting';
import { applyDefaultAclModuleOptions } from '@/utilities/aclDefaultModuleOptions';
import {
  areAclEditOptionsConfigured,
  resolveAclOptionSelection,
  isAclModuleOptionsRelevant,
  shouldOpenAclOptionsModal,
} from '@/utilities/aclModuleOptionsAccess';

const ACL_BOOLEAN_OPS = ['read', 'add', 'edit', 'delete', 'view_permission'];

const ALL_DEFAULT_ACL_ROLES = [
  USER_ROLES.RESELLER,
  USER_ROLES.COMPANY_ADMIN,
  USER_ROLES.CALL_CENTRE_ADMIN,
  USER_ROLES.CALL_CENTRE_AGENT,
  USER_ROLES.CALL_CENTRE_TEAM_LEAD,
];


function getApiUrl(url, id) {
  return url.replace(':id', id);
}

const ACLSettingEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [aclName, setACLName] = useState('');
  const [aclRecord, setAclRecord] = useState<any>(null);
  const [showFreezePrompt, setShowFreezePrompt] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [moduleList, setModuleList] = useState([]);
  const [selectedModules, setSelectedModules] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modulePermission, updateModulePermission] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [defaultForRole, setDefaultForRole] = useState('');
  const [defaultAclRoles, setDefaultAclRoles] = useState<any[]>([]);
  const [showVoicePlanModal, setShowVoicePlanModal] = useState(false);
  const [showVoiceLibraryModal, setShowVoiceLibraryModal] = useState(false);
  const [showCallGroupModal, setShowCallGroupModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showIvrModal, setShowIvrModal] = useState(false);
  const [showCallCentreVoicemailModal, setShowCallCentreVoicemailModal] = useState(false);
  const [showCompanyAssetsVisibilityModal, setShowCompanyAssetsVisibilityModal] = useState(false);
  const assetsVisibilityEligibility = useAssetsVisibilityEligibility();
  const [showUsersEmailVerificationModal, setShowUsersEmailVerificationModal] = useState(false);
  const [showAgentLeadsCustomModal, setShowAgentLeadsCustomModal] = useState(false);
  const [showDispositionFormsTimingModal, setShowDispositionFormsTimingModal] = useState(false);
  const [showTtsOperatorModal, setShowTtsOperatorModal] = useState(false);
  const [showAiAgentDescLimitModal, setShowAiAgentDescLimitModal] = useState(false);
  const [masterLanguages, setMasterLanguages] = useState([]);
  const [cloneSourceOps, setCloneSourceOps] = useState(null);
  const [cloneSourceName, setCloneSourceName] = useState('');

  const ceilingOps = cloneSourceOps || modulePermission;
  const ceilingIsSuperAdmin = isSuperAdmin && !cloneSourceOps;

  const isOpDisabledByClone = (moduleName, op) => {
    if (!cloneSourceOps) return false;
    return cloneSourceOps?.[moduleName]?.[op] !== true;
  };
  const [aclPage, setAclPage] = useState(1);
  const [aclPageSize, setAclPageSize] = useState(10);
  const [aclFilter, setAclFilter] = useState('all');

  const parentGrantableDirections = useMemo(
    () => getParentGrantablePlanDirections(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const voicePlanDirectionOptions = useMemo(
    () => getVoicePlanDirectionOptionsForEditor(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const parentGrantableVoiceLibrary = useMemo(
    () => getParentGrantableVoiceLibraryRestrictions(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const voiceLibraryMasterLists = useMemo(() => ({ languages: masterLanguages }), [masterLanguages]);
  const voiceTypeOptionsForEditor = useMemo(
    () => getVoiceTypeOptionsForEditor(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const languageOptionsForEditor = useMemo(
    () => getLanguageOptionsForEditor(ceilingOps, ceilingIsSuperAdmin, masterLanguages),
    [ceilingOps, ceilingIsSuperAdmin, masterLanguages],
  );
  const parentGrantableCallGroupOptions = useMemo(
    () => getParentGrantableCallGroupEditOptions(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const callGroupEditOptionDefs = useMemo(
    () => getCallGroupEditOptionsForEditor(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const parentGrantableAgentOptions = useMemo(
    () => getParentGrantableAgentEditOptions(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const agentEditOptionDefs = useMemo(
    () => getAgentEditOptionsForEditor(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const parentGrantableIvrOptions = useMemo(
    () => getParentGrantableIvrNodeOptions(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const ivrNodeOptionDefs = useMemo(
    () => getIvrNodeOptionsForEditor(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const parentGrantableCallCentreVoicemail = useMemo(
    () => getParentGrantableCallCentreVoicemail(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const parentGrantableCallCentreTranscription = useMemo(
    () => getParentGrantableCallCentreTranscription(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const parentGrantableCustomLeads = useMemo(
    () => getParentGrantableCustomLeads(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );

  const parentGrantableAssetsVisibility = useMemo(
    () => ({
      canGrantAssetsVisibility:
        isSuperAdmin || assetsVisibilityEligibility.canGrantAssetsVisibility,
    }),
    [isSuperAdmin, assetsVisibilityEligibility.canGrantAssetsVisibility],
  );
  const parentGrantableUsersEmailVerification = useMemo(
    () => getParentGrantableUsersEmailVerification(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const parentGrantableDispositionTimingOptions = useMemo(
    () => getParentGrantableDispositionTimingOptions(ceilingOps, ceilingIsSuperAdmin),
    [ceilingOps, ceilingIsSuperAdmin],
  );
  const canGrantDispositionCustomTiming = useMemo(
    () => parentGrantableDispositionTimingOptions.includes(CUSTOM_TIMING_ACL_OPTION),
    [parentGrantableDispositionTimingOptions],
  );
  const canGrantDispositionFollowUps = useMemo(
    () => parentGrantableDispositionTimingOptions.includes(ALLOW_FOLLOWUPS_ACL_OPTION),
    [parentGrantableDispositionTimingOptions],
  );

  useEffect(() => {
    async function superadminCheck() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User not found in Storage');
        return null;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in user from Storage', e);
        return null;
      }
      if (user?.acl_settings?.module_operations) {
        updateModulePermission(parseModuleOperations(user.acl_settings.module_operations));
      }
      if (user.role === USER_ROLES.SUPERADMIN) setIsSuperAdmin(true);
      setLoading(false);
    }
    superadminCheck();
  }, []);

  useEffect(() => {
    const editApiUrl = getApiUrl(API_ACL_SETTING_EDIT, id);
    getApiCall(editApiUrl, {}).then(onGetSuccess).catch(onError);
  }, [id]);

  useEffect(() => {
    if (!loading) {
      getApiCall(API_MODULE_LIST, {}).then(onModuleListSuccess).catch(onError);

      getApiCall(API_ACL_SETTING_LIST, {})
        .then((resp: any) =>
          setDefaultAclRoles(
            (resp || [])
              .filter((item: any) => item.default_for_role)
              .map((item: any) => item.default_for_role),
          ),
        )
        .catch(onError);
    }
  }, [loading]);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  useEffect(() => {
    getApiCall('/v2/tts-languages', {})
      .then((response) => {
        if (Array.isArray(response)) {
          setMasterLanguages(
            response.map((lang) => ({ label: lang.language, value: lang.code })),
          );
        }
      })
      .catch(() => {});
  }, []);

  function onGetSuccess(resp) {
    const user = getUser();
    const userIsSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

    if (!userIsSuperAdmin && resp?.default_for_role) {
      toast.error('Only Super Admin can edit default ACLs.');
      navigate(`/${ROUTES.ACL_SETTING.LIST}`);
      return;
    }

    if (!userIsSuperAdmin && isAclLocked(resp)) {
      toast.error('This ACL is frozen. Only Super Admin can change it.');
      navigate(`/${ROUTES.ACL_SETTING.LIST}`);
      return;
    }

    setAclRecord(resp || null);

    if (resp?.is_cloned && resp?.cloned_from_acl_id) {
      getApiCall(getApiUrl(API_ACL_SETTING_EDIT, resp.cloned_from_acl_id), {})
        .then((source) => {
          const rawSourceOps = source?.module_operations;
          setCloneSourceName(source?.acl_name || '');
          setCloneSourceOps(
            typeof rawSourceOps === 'string' ? JSON.parse(rawSourceOps) : rawSourceOps || {},
          );
        })
        .catch(() => {});
    }
    setACLName(resp?.acl_name);
    const rawOps = resp?.module_operations;
    const parsed = typeof rawOps === 'string' ? JSON.parse(rawOps) : rawOps || {};
    setSelectedModules(parsed);
    setDefaultForRole(resp?.default_for_role || '');
  }

  function getKeysWithTrueValues(moduleOperations) {
    return Object.keys(moduleOperations).filter((key) => {
      const permissions = moduleOperations[key];
      return ACL_BOOLEAN_OPS.some((op) => permissions[op] === true);
    });
  }

  const isVoicePlanRow = (moduleName) => moduleName === VOICE_PLAN_ACL_MODULE;
  const isVoiceLibraryRow = (moduleName) => moduleName === VOICE_LIBRARY_ACL_MODULE;
  const isCallGroupRow = (moduleName) => moduleName === CALL_GROUP_ACL_MODULE;
  const isAgentsRow = (moduleName) => moduleName === AGENT_ACL_MODULE;
  const isIvrRow = (moduleName) => moduleName === IVR_ACL_MODULE;
  const isCallCentreRow = (moduleName) => moduleName === CALL_CENTRE_ACL_MODULE;
  const isUsersRow = (moduleName) => moduleName === USERS_ACL_MODULE;
  const isDispositionFormsRow = (moduleName) => moduleName === DISPOSITION_FORMS_ACL_MODULE;
  const isTtsRow = (moduleName) => moduleName === TTS_ACL_MODULE;
  const isAiAgentRow = (moduleName) => moduleName === AI_AGENT_ACL_MODULE;
  const isCompanyRow = (moduleName) => moduleName === COMPANY_ACL_MODULE;
  const isAgentLeadsRow = (moduleName) => moduleName === AGENT_LEADS_ACL_MODULE;

  const voicePlanAccessEnabled = (moduleName) =>
    isVoicePlanRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const voiceLibraryAccessEnabled = (moduleName) =>
    isVoiceLibraryRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const callGroupEditAccessEnabled = (moduleName) =>
    isCallGroupRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const agentSettingsAccessEnabled = (moduleName) =>
    isAgentsRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const ivrSettingsAccessEnabled = (moduleName) =>
    isIvrRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const callCentreVoicemailAccessEnabled = (moduleName) =>
    isCallCentreRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const usersEmailVerificationAccessEnabled = (moduleName) =>
    isUsersRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const dispositionTimingAccessEnabled = (moduleName) =>
    isDispositionFormsRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const ttsAccessEnabled = (moduleName) =>
    isTtsRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);
  const aiAgentDescLimitAccessEnabled = (moduleName) =>
    isAiAgentRow(moduleName) && isAclModuleOptionsRelevant(selectedModules[moduleName]);

  const agentLeadsCustomAccessEnabled = (moduleName) =>
    isAgentLeadsRow(moduleName) && isAgentLeadsCustomRelevant(selectedModules[moduleName]);

  const companyAssetsVisibilityAccessEnabled = (moduleName) =>
    isCompanyRow(moduleName) &&
    parentGrantableAssetsVisibility.canGrantAssetsVisibility &&
    isAclModuleOptionsRelevant(selectedModules[moduleName]);

  const openOptionsModalForModule = (moduleName) => {
    if (isVoicePlanRow(moduleName)) setShowVoicePlanModal(true);
    else if (isVoiceLibraryRow(moduleName)) setShowVoiceLibraryModal(true);
    else if (isCallGroupRow(moduleName)) setShowCallGroupModal(true);
    else if (isAgentsRow(moduleName)) setShowAgentModal(true);
    else if (isIvrRow(moduleName)) setShowIvrModal(true);
    else if (isCallCentreRow(moduleName)) setShowCallCentreVoicemailModal(true);
    else if (isUsersRow(moduleName)) setShowUsersEmailVerificationModal(true);
    else if (isAgentLeadsRow(moduleName)) setShowAgentLeadsCustomModal(true);
    else if (isDispositionFormsRow(moduleName)) setShowDispositionFormsTimingModal(true);
    else if (isTtsRow(moduleName)) setShowTtsOperatorModal(true);
    else if (isAiAgentRow(moduleName)) setShowAiAgentDescLimitModal(true);
    else if (isCompanyRow(moduleName) && parentGrantableAssetsVisibility.canGrantAssetsVisibility)
      setShowCompanyAssetsVisibilityModal(true);
  };

  function onModuleListSuccess(resp) {
    if (isSuperAdmin) {
      setModuleList(resp);
    } else {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        let modules = getKeysWithTrueValues(parseModuleOperations(user.acl_settings.module_operations));
        modules = resp.filter((item) => modules.includes(item.module_name));
        setModuleList(modules);
      }
    }
  }

  function validateForm() {
    let valid = true;
    if (!aclName.trim()) {
      valid = false;
      toast.error('ACL name required');
    }

    const vpEntry = selectedModules[VOICE_PLAN_ACL_MODULE];
    if (vpEntry && isVoicePlanAclActive(vpEntry) && Array.isArray(vpEntry.plan_directions)) {
      const dirs = normalizeVoicePlanDirectionsForSave(vpEntry.plan_directions, parentGrantableDirections);
      if (dirs.length === 0) {
        valid = false;
        toast.error('Voice Plans: configure at least one plan type you are allowed to grant.');
      }
    }

    const vlEntry = selectedModules[VOICE_LIBRARY_ACL_MODULE];
    if (
      vlEntry &&
      isVoiceLibraryAclActive(vlEntry) &&
      (Array.isArray(vlEntry.voice_types) || Array.isArray(vlEntry.languages))
    ) {
      const normalized = normalizeVoiceLibraryAclForSave(vlEntry, parentGrantableVoiceLibrary, voiceLibraryMasterLists);
      if (normalized.voice_types.length === 0 || normalized.languages.length === 0) {
        valid = false;
        toast.error('Voice Library: configure at least one voice type and language.');
      }
    }

    const cgEntry = selectedModules[CALL_GROUP_ACL_MODULE];
    if (cgEntry && isAclModuleOptionsRelevant(cgEntry) && areAclEditOptionsConfigured(cgEntry)) {
      const options = normalizeCallGroupEditOptionsForSave(cgEntry.edit_options, parentGrantableCallGroupOptions);
      if (options.length === 0) {
        valid = false;
        toast.error('Call Group: configure at least one edit option you are allowed to grant.');
      }
    }

    const agentEntry = selectedModules[AGENT_ACL_MODULE];
    if (agentEntry && isAclModuleOptionsRelevant(agentEntry) && areAclEditOptionsConfigured(agentEntry)) {
      const options = normalizeAgentEditOptionsForSave(agentEntry.edit_options, parentGrantableAgentOptions);
      if (options.length === 0) {
        valid = false;
        toast.error('Agents: configure at least one create/edit option you are allowed to grant.');
      }
    }

    const ivrEntry = selectedModules[IVR_ACL_MODULE];
    if (ivrEntry && isAclModuleOptionsRelevant(ivrEntry) && areAclEditOptionsConfigured(ivrEntry)) {
      const options = normalizeIvrNodeOptionsForSave(ivrEntry.edit_options, parentGrantableIvrOptions);
      if (options.length === 0) {
        valid = false;
        toast.error('IVR: configure at least one node option you are allowed to grant.');
      }
    }



    return valid;
  }

  const normalizePermissions = (modules) => {
    const normalized = {};
    for (const module in modules) {
      normalized[module] = {};
      ACL_BOOLEAN_OPS.forEach((op) => {
        normalized[module][op] = isAclOpSupported(module, op) ? (modules[module]?.[op] ?? false) : false;
      });

      if (module === VOICE_PLAN_ACL_MODULE && isVoicePlanAclActive(modules[module])) {
        const dirs = normalizeVoicePlanDirectionsForSave(
          resolveAclOptionSelection(
            modules[module].plan_directions,
            voicePlanDirectionOptions.map((o) => o.value),
          ),
          parentGrantableDirections,
        );
        if (dirs.length > 0) normalized[module].plan_directions = dirs;
      }
      if (module === VOICE_LIBRARY_ACL_MODULE && isVoiceLibraryAclActive(modules[module])) {
        const vl = normalizeVoiceLibraryAclForSave(
          {
            voice_types: resolveAclOptionSelection(
              modules[module].voice_types,
              voiceTypeOptionsForEditor.map((o) => o.value),
            ),
            languages: resolveAclOptionSelection(
              modules[module].languages,
              languageOptionsForEditor.map((o) => o.value),
            ),
          },
          parentGrantableVoiceLibrary,
          voiceLibraryMasterLists,
        );
        if (vl.voice_types.length > 0) normalized[module].voice_types = vl.voice_types;
        if (vl.languages.length > 0) normalized[module].languages = vl.languages;
      }
      if (module === CALL_GROUP_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        const options = normalizeCallGroupEditOptionsForSave(
          resolveAclOptionSelection(
            modules[module].edit_options,
            callGroupEditOptionDefs.map((o) => o.value),
            DEFAULT_CALL_GROUP_EDIT_OPTIONS,
          ),
          parentGrantableCallGroupOptions,
        );
        if (options.length > 0) normalized[module].edit_options = options;
      }
      if (module === AGENT_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        const options = normalizeAgentEditOptionsForSave(
          resolveAclOptionSelection(
            modules[module].edit_options,
            agentEditOptionDefs.map((o) => o.value),
            DEFAULT_AGENT_EDIT_OPTIONS,
          ),
          parentGrantableAgentOptions,
        );
        if (options.length > 0) normalized[module].edit_options = options;
      }
      if (module === IVR_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        const options = normalizeIvrNodeOptionsForSave(
          resolveAclOptionSelection(
            modules[module].edit_options,
            ivrNodeOptionDefs.map((o) => o.value),
            DEFAULT_IVR_NODE_OPTIONS,
          ),
          parentGrantableIvrOptions,
        );
        if (options.length > 0) normalized[module].edit_options = options;
      }
      if (module === CALL_CENTRE_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        const ccVm = normalizeCallCentreVoicemailAclForSave(
          { edit_options: modules[module].edit_options },
          parentGrantableCallCentreVoicemail,
        );
        const ccTr = normalizeCallCentreTranscriptionAclForSave(
          { edit_options: modules[module].edit_options },
          parentGrantableCallCentreTranscription,
        );
        normalized[module].edit_options = [
          ...new Set([...ccVm.edit_options, ...ccTr.edit_options]),
        ];
        delete normalized[module].storage_quota;
      }

      if (module === COMPANY_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        const assets = normalizeCompanyAssetsVisibilityAclForSave(
          { edit_options: modules[module].edit_options },
          parentGrantableAssetsVisibility,
        );
        normalized[module].edit_options = assets.edit_options;
      }
      if (module === AGENT_LEADS_ACL_MODULE && isAgentLeadsCustomRelevant(modules[module])) {
        const custom = normalizeAgentLeadsCustomAclForSave(
          {
            edit_options: Array.isArray(modules[module].edit_options)
              ? modules[module].edit_options
              : (parentGrantableCustomLeads.canGrantCustomLeads ? [CUSTOM_LEADS_ACL_OPTION] : []),
          },
          parentGrantableCustomLeads,
        );
        normalized[module].edit_options = custom.edit_options;
      }
      if (module === USERS_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        const usersEv = normalizeUsersEmailVerificationAclForSave(
          { edit_options: modules[module].edit_options },
          parentGrantableUsersEmailVerification,
        );
        normalized[module].edit_options = usersEv.edit_options;
      }
      if (module === DISPOSITION_FORMS_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        const options = normalizeDispositionTimingOptionsForSave(
          resolveAclOptionSelection(
            modules[module].edit_options,
            parentGrantableDispositionTimingOptions,
            DEFAULT_DISPOSITION_TIMING_OPTIONS,
          ),
          parentGrantableDispositionTimingOptions,
        );
        normalized[module].edit_options = options;
      }
      if (module === TTS_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        normalized[module].tts_operator = normalizeTtsOperatorForSave(modules[module].tts_operator);
        normalized[module].allowed_languages = Array.isArray(modules[module].allowed_languages)
          ? modules[module].allowed_languages
          : [];
      }
      if (module === AI_AGENT_ACL_MODULE && isAclModuleOptionsRelevant(modules[module])) {
        normalized[module].description_char_limit = normalizeAiAgentDescLimitForSave(
          modules[module].description_char_limit,
        );
      }
    }
    return normalized;
  };

  async function submitForm(freezePassword) {
    setButtonDisable(true);
    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    if (isAclLocked(aclRecord) && !freezePassword) {
      setButtonDisable(false);
      setShowFreezePrompt(true);
      return;
    }

    const params = {
      acl_name: aclName,
      module_operations: normalizePermissions(selectedModules),
      default_for_role: isSuperAdmin ? defaultForRole || null : null,
      ...(freezePassword ? { freeze_password: freezePassword } : {}),
    };
    const editApiUrl = getApiUrl(API_ACL_SETTING_EDIT, id);
    return postApiCall(editApiUrl, params)
      .then(onAClSettingUpdateSuccess)
      .catch(onAClSettingUpdateError);
  }

  function onAClSettingUpdateSuccess() {
    setShowFreezePrompt(false);
    toast.success('ACL Setting updated successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.ACL_SETTING.LIST}`);
    }, 1500);
  }

  function onAClSettingUpdateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err));
    }
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err));
    }
  }

  const handleCheckboxChange = (moduleName, operation) => {
    if (!isAclOpSupported(moduleName, operation)) return;
    if (isOpDisabledByClone(moduleName, operation)) {
      toast.error('This permission is not allowed on the ACL this one was copied from.');
      return;
    }
    setSelectedModules((prev) => {
      const current = {
        read: prev?.[moduleName]?.read ?? false,
        add: prev?.[moduleName]?.add ?? false,
        edit: prev?.[moduleName]?.edit ?? false,
        delete: prev?.[moduleName]?.delete ?? false,
        view_permission: prev?.[moduleName]?.view_permission ?? false,
        plan_directions: prev?.[moduleName]?.plan_directions,
        voice_types: prev?.[moduleName]?.voice_types,
        categories: prev?.[moduleName]?.categories,
        languages: prev?.[moduleName]?.languages,
        edit_options: prev?.[moduleName]?.edit_options,
        tts_operator: prev?.[moduleName]?.tts_operator,
        description_char_limit: prev?.[moduleName]?.description_char_limit,
      };

      const nextValue = !current[operation];
      const nextEntry = { ...current, [operation]: nextValue };

      if (isVoicePlanRow(moduleName) && !isVoicePlanAclActive(nextEntry)) delete nextEntry.plan_directions;
      if (isVoiceLibraryRow(moduleName) && !isVoiceLibraryAclActive(nextEntry)) {
        delete nextEntry.voice_types;
        delete nextEntry.categories;
        delete nextEntry.languages;
      }
      if (
        (isCallGroupRow(moduleName) ||
          isAgentsRow(moduleName) ||
          isIvrRow(moduleName) ||
          isCallCentreRow(moduleName) ||
          isUsersRow(moduleName) ||
          isCompanyRow(moduleName) ||
          isDispositionFormsRow(moduleName)) &&
        !isAclModuleOptionsRelevant(nextEntry)
      ) {
        delete nextEntry.edit_options;
      }
      if (isAgentLeadsRow(moduleName) && !isAgentLeadsCustomRelevant(nextEntry)) {
        delete nextEntry.edit_options;
      }
      if (isTtsRow(moduleName) && !isAclModuleOptionsRelevant(nextEntry)) delete nextEntry.tts_operator;
      if (isAiAgentRow(moduleName) && !isAclModuleOptionsRelevant(nextEntry)) delete nextEntry.description_char_limit;

      const nextEntryWithDefaults = applyDefaultAclModuleOptions(moduleName, nextEntry, {
        parentGrantableCallGroupOptions,
        parentGrantableAgentOptions,
        parentGrantableIvrOptions,
        parentGrantableCallCentreVoicemail,
        parentGrantableCallCentreTranscription,
        parentGrantableUsersEmailVerification,
        parentGrantableDispositionTimingOptions,
        parentCanGrantCustomLeads: parentGrantableCustomLeads.canGrantCustomLeads,
        parentGrantableDirections,
        voiceTypeOptionsForEditor,
        languageOptionsForEditor,
      });

      const nextState = { ...prev, [moduleName]: nextEntryWithDefaults };

      if (shouldOpenAclOptionsModal(moduleName, operation, nextValue)) {
        setTimeout(() => openOptionsModalForModule(moduleName), 0);
      }
      return nextState;
    });
  };

  const saveVoicePlanDirections = (directions) => {
    const dirs = normalizeVoicePlanDirectionsForSave(directions, parentGrantableDirections);
    setSelectedModules((prev) => ({
      ...prev,
      [VOICE_PLAN_ACL_MODULE]: { ...(prev[VOICE_PLAN_ACL_MODULE] || {}), plan_directions: dirs },
    }));
  };

  const saveVoiceLibraryAcl = (values) => {
    const normalized = normalizeVoiceLibraryAclForSave(values, parentGrantableVoiceLibrary, voiceLibraryMasterLists);
    setSelectedModules((prev) => ({
      ...prev,
      [VOICE_LIBRARY_ACL_MODULE]: {
        ...(prev[VOICE_LIBRARY_ACL_MODULE] || {}),
        voice_types: normalized.voice_types,
        categories: normalized.categories,
        languages: normalized.languages,
      },
    }));
  };

  const saveCallGroupEditOptions = (options) => {
    const normalized = normalizeCallGroupEditOptionsForSave(options, parentGrantableCallGroupOptions);
    setSelectedModules((prev) => ({
      ...prev,
      [CALL_GROUP_ACL_MODULE]: { ...(prev[CALL_GROUP_ACL_MODULE] || {}), edit_options: normalized },
    }));
  };

  const saveAgentEditOptions = (options) => {
    const normalized = normalizeAgentEditOptionsForSave(options, parentGrantableAgentOptions);
    setSelectedModules((prev) => ({
      ...prev,
      [AGENT_ACL_MODULE]: { ...(prev[AGENT_ACL_MODULE] || {}), edit_options: normalized },
    }));
  };

  const saveCompanyAssetsVisibilityAcl = ({ edit_options }) => {
    setSelectedModules((prev) => ({
      ...prev,
      [COMPANY_ACL_MODULE]: { ...(prev[COMPANY_ACL_MODULE] || {}), edit_options: edit_options || [] },
    }));
  };

  const saveCallCentreVoicemailAcl = ({ edit_options }) => {
    setSelectedModules((prev) => {
      const entry = { ...(prev[CALL_CENTRE_ACL_MODULE] || {}) };
      entry.edit_options = edit_options || [];
      delete entry.storage_quota;
      return { ...prev, [CALL_CENTRE_ACL_MODULE]: entry };
    });
  };

  const saveAgentLeadsCustomAcl = ({ edit_options }) => {
    setSelectedModules((prev) => {
      const entry = { ...(prev[AGENT_LEADS_ACL_MODULE] || {}) };
      entry.edit_options = edit_options || [];
      return { ...prev, [AGENT_LEADS_ACL_MODULE]: entry };
    });
  };

  const saveUsersEmailVerificationAcl = ({ edit_options }) => {
    setSelectedModules((prev) => {
      const entry = { ...(prev[USERS_ACL_MODULE] || {}) };
      entry.edit_options = edit_options || [];
      return { ...prev, [USERS_ACL_MODULE]: entry };
    });
  };

  const saveDispositionTimingOptions = (options) => {
    const normalized = normalizeDispositionTimingOptionsForSave(options, parentGrantableDispositionTimingOptions);
    setSelectedModules((prev) => ({
      ...prev,
      [DISPOSITION_FORMS_ACL_MODULE]: { ...(prev[DISPOSITION_FORMS_ACL_MODULE] || {}), edit_options: normalized },
    }));
  };

  const saveIvrNodeOptions = (options) => {
    const normalized = normalizeIvrNodeOptionsForSave(options, parentGrantableIvrOptions);
    setSelectedModules((prev) => ({
      ...prev,
      [IVR_ACL_MODULE]: { ...(prev[IVR_ACL_MODULE] || {}), edit_options: normalized },
    }));
  };

  const saveTtsOperator = (operator, languages) => {
    setSelectedModules((prev) => ({
      ...prev,
      [TTS_ACL_MODULE]: {
        ...(prev[TTS_ACL_MODULE] || {}),
        tts_operator: normalizeTtsOperatorForSave(operator),
        ...(Array.isArray(languages) && languages.length > 0
          ? { allowed_languages: languages }
          : { allowed_languages: [] }),
      },
    }));
  };

  const saveAiAgentDescLimit = (limit) => {
    setSelectedModules((prev) => ({
      ...prev,
      [AI_AGENT_ACL_MODULE]: {
        ...(prev[AI_AGENT_ACL_MODULE] || {}),
        description_char_limit: normalizeAiAgentDescLimitForSave(limit),
      },
    }));
  };

  const sortedModules = useMemo(
    () =>
      moduleList
        .slice()
        .sort((a, b) =>
          getAclModuleDisplayLabel(a.module_name).localeCompare(getAclModuleDisplayLabel(b.module_name)),
        ),
    [moduleList],
  );

  const agentLeadsGrantable = canGrantAgentLeadsAcl(getUser()?.role, defaultForRole);

  const filteredModules = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return sortedModules.filter((module) => {
      if (module.module_name === AGENT_LEADS_ACL_MODULE && !agentLeadsGrantable) return false;
      const key = module.module_name?.toLowerCase() ?? '';
      const display = getAclModuleDisplayLabel(module.module_name)?.toLowerCase() ?? '';
      const matchesSearch = !term || key.includes(term) || display.includes(term);
      if (!matchesSearch) return false;
      const viewAllowed = selectedModules[module.module_name]?.view_permission === true;
      if (aclFilter === 'allowed' && !viewAllowed) return false;
      if (aclFilter === 'denied' && viewAllowed) return false;
      return true;
    });
  }, [sortedModules, searchTerm, aclFilter, selectedModules, agentLeadsGrantable]);

  useEffect(() => {
    setAclPage(1);
  }, [searchTerm, aclFilter, moduleList.length]);

  const optionsCellContext = useMemo(
    () => ({
      selectedModules,
      isVoicePlanRow,
      isVoiceLibraryRow,
      isCallGroupRow,
      isAgentsRow,
      isCallCentreRow,
      isCompanyRow,
      companyAssetsVisibilityAccessEnabled,
      formatCompanyAssetsVisibilityAclLabel,
      setShowCompanyAssetsVisibilityModal,
      isIvrRow,
      isUsersRow,
      isDispositionFormsRow,
      voicePlanAccessEnabled,
      voiceLibraryAccessEnabled,
      callGroupEditAccessEnabled,
      agentSettingsAccessEnabled,
      callCentreVoicemailAccessEnabled,
      usersEmailVerificationAccessEnabled,
      dispositionTimingAccessEnabled,
      ivrSettingsAccessEnabled,
      formatPlanDirectionsLabel,
      formatVoiceLibraryAclLabel,
      formatCallGroupEditOptionsLabel,
      formatAgentEditOptionsLabel,
      formatCallCentreVoicemailAclLabel,
      formatCallCentreTranscriptionAclLabel,
      isAgentLeadsRow,
      agentLeadsCustomAccessEnabled,
      formatAgentLeadsCustomAclLabel,
      setShowAgentLeadsCustomModal,
      formatUsersEmailVerificationAclLabel,
      formatDispositionTimingAclLabel,
      formatIvrNodeOptionsLabel,
      setShowVoicePlanModal,
      setShowVoiceLibraryModal,
      setShowCallGroupModal,
      setShowAgentModal,
      setShowCallCentreVoicemailModal,
      setShowUsersEmailVerificationModal,
      setShowDispositionFormsTimingModal,
      setShowIvrModal,
      isTtsRow,
      ttsAccessEnabled,
      formatTtsOperatorLabel,
      setShowTtsOperatorModal,
      isAiAgentRow,
      aiAgentDescLimitAccessEnabled,
      formatAiAgentDescLimitAclLabel,
      setShowAiAgentDescLimitModal,
    }),
    [selectedModules],
  );

  const handleGlobalSelectAll = () => {
    setSelectedModules((prev) => {
      const next = { ...prev };
      filteredModules.forEach((m) => {
        const name = m.module_name;
        next[name] = {
          ...(prev[name] || {}),
          read: isAclOpSupported(name, 'read'),
          add: isAclOpSupported(name, 'add'),
          edit: isAclOpSupported(name, 'edit'),
          delete: isAclOpSupported(name, 'delete'),
          view_permission: isAclOpSupported(name, 'view_permission'),
        };
      });
      return next;
    });
  };

  const handleGlobalDeselectAll = () => {
    setSelectedModules((prev) => {
      const next = { ...prev };
      filteredModules.forEach((m) => {
        const name = m.module_name;
        next[name] = {
          ...(prev[name] || {}),
          read: false,
          add: false,
          edit: false,
          delete: false,
          view_permission: false,
        };
      });
      return next;
    });
  };

  const handleSelectAllPermissions = (moduleName) => {
    setSelectedModules((prev) => {
      const current = prev[moduleName] || {};
      const isAllSelected = ACL_BOOLEAN_OPS
        .filter((op) => isAclOpSupported(moduleName, op))
        .every((op) => current[op]);
      const allow = (op) => !isAllSelected && isAclOpSupported(moduleName, op) && !isOpDisabledByClone(moduleName, op);
      return {
        ...prev,
        [moduleName]: {
          ...current,
          read: allow('read'),
          add: allow('add'),
          edit: allow('edit'),
          delete: allow('delete'),
          view_permission: allow('view_permission'),
        },
      };
    });
  };

  return (
    <div className="acl-settings-page min-h-0 flex-1 overflow-y-auto px-6 pb-8">
      <div className="row">
        <div className="col-12">
          <ShadcnCard className="mt-4">
            <div className="p-4">
              <div className="new-user-info">
                <form onSubmit={(e) => e.preventDefault()}>
                  <AclSettingsPageHeader
                    title="Edit ACL Settings"
                    subtitle="Manage module access and permissions."
                    onBack={() => navigate(`/${ROUTES.ACL_SETTING.LIST}`)}
                    onHelpClick={() =>
                      toast.info(
                        'Enable View Permission to show a module in the user sidebar. Use Options to configure granular permissions.',
                      )
                    }
                  />

                  <AclSettingsFormTop
                    aclName={aclName}
                    onAclNameChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z0-9_ ]*$/.test(value)) {
                        setACLName(value);
                      }
                    }}
                    showDefaultRole={
                      isSuperAdmin &&
                      ALL_DEFAULT_ACL_ROLES.some((role) => !defaultAclRoles.includes(role))
                    }
                    defaultForRole={defaultForRole}
                    onDefaultRoleChange={(e) => setDefaultForRole(e.target.value)}
                    defaultRoleOptions={
                      <>
                        <option value="">None</option>
                        <option value={USER_ROLES.RESELLER}>Reseller</option>
                        <option value={USER_ROLES.COMPANY_ADMIN}>Company Admin</option>
                        <option value={USER_ROLES.CALL_CENTRE_ADMIN}>Call Center Admin</option>
                        <option value={USER_ROLES.CALL_CENTRE_AGENT}>Call Center Agent</option>
                        <option value={USER_ROLES.CALL_CENTRE_TEAM_LEAD}>Team Lead</option>
                      </>
                    }
                  />

                  {/* {aclRecord?.is_cloned ? (
                    <div className="acl-flash-note">
                      <strong>Copied ACL:</strong> this ACL was copied from{' '}
                      {cloneSourceName || `ACL #${aclRecord.cloned_from_acl_id}`}
                      {aclRecord.cloned_at ? ` on ${new Date(aclRecord.cloned_at).toLocaleString()}` : ''}.
                      Permissions can only be turned off &mdash; anything not allowed on the
                      source ACL stays locked.
                    </div>
                  ) : null} */}

                  {!defaultForRole ? (
                    <div className="acl-flash-note">
                      <strong>Note:</strong> This ACL is private to your account &mdash;
                      only you can view and manage it. You can still assign it to the
                      users you create.
                    </div>
                  ) : null}

                  <div className="acl-section-heading">
                    <h5>Module Permissions</h5>
                    <p>Configure access permissions for each module.</p>
                  </div>

                  <AclModulePermissionsToolbar
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    onSelectAllModules={handleGlobalSelectAll}
                    onDeselectAllModules={handleGlobalDeselectAll}
                    filterValue={aclFilter}
                    onFilterChange={setAclFilter}
                  />

                  {moduleList.length === 0 ? (
                    <div className="text-muted py-4">No modules added</div>
                  ) : (
                    <AclModulePermissionsTable
                      modules={filteredModules}
                      selectedModules={selectedModules}
                      onPermissionChange={handleCheckboxChange}
                      onSelectAllRow={handleSelectAllPermissions}
                      isOpDisabled={cloneSourceOps ? isOpDisabledByClone : undefined}
                      renderOptionsCell={(moduleName) => renderAclOptionsCell(moduleName, optionsCellContext)}
                      page={aclPage}
                      pageSize={aclPageSize}
                      onPageChange={setAclPage}
                      onPageSizeChange={(size) => {
                        setAclPageSize(size);
                        setAclPage(1);
                      }}
                    />
                  )}

                  <VoicePlanDirectionsModal
                    show={showVoicePlanModal}
                    onHide={() => setShowVoicePlanModal(false)}
                    initialDirections={selectedModules[VOICE_PLAN_ACL_MODULE]?.plan_directions || []}
                    directionOptions={voicePlanDirectionOptions}
                    onSave={saveVoicePlanDirections}
                  />

                  <CallGroupEditAclModal
                    show={showCallGroupModal}
                    onHide={() => setShowCallGroupModal(false)}
                    initialOptions={selectedModules[CALL_GROUP_ACL_MODULE]?.edit_options || []}
                    optionDefs={callGroupEditOptionDefs}
                    limitedByParent={
                      !isSuperAdmin && parentGrantableCallGroupOptions.length < callGroupEditOptionDefs.length
                    }
                    onSave={saveCallGroupEditOptions}
                  />

                  <AgentEditAclModal
                    show={showAgentModal}
                    onHide={() => setShowAgentModal(false)}
                    initialOptions={selectedModules[AGENT_ACL_MODULE]?.edit_options || []}
                    optionDefs={agentEditOptionDefs}
                    limitedByParent={
                      !isSuperAdmin && parentGrantableAgentOptions.length < agentEditOptionDefs.length
                    }
                    onSave={saveAgentEditOptions}
                  />

                  <CallCentreVoicemailAclModal
                    show={showCallCentreVoicemailModal}
                    onHide={() => setShowCallCentreVoicemailModal(false)}
                    initialOptions={selectedModules[CALL_CENTRE_ACL_MODULE]?.edit_options}
                    optionsConfigured={areAclEditOptionsConfigured(selectedModules[CALL_CENTRE_ACL_MODULE])}
                    canGrantVoicemail={parentGrantableCallCentreVoicemail.canGrantVoicemail}
                    canGrantTranscription={parentGrantableCallCentreTranscription.canGrantTranscription}
                    limitedByParent={
                      !isSuperAdmin &&
                      (!parentGrantableCallCentreVoicemail.canGrantVoicemail ||
                        !parentGrantableCallCentreTranscription.canGrantTranscription)
                    }
                    onSave={saveCallCentreVoicemailAcl}
                  />

                  <CompanyAssetsVisibilityAclModal
                    show={showCompanyAssetsVisibilityModal}
                    onHide={() => setShowCompanyAssetsVisibilityModal(false)}
                    initialOptions={selectedModules[COMPANY_ACL_MODULE]?.edit_options}
                    optionsConfigured={areAclEditOptionsConfigured(selectedModules[COMPANY_ACL_MODULE])}
                    canGrantAssetsVisibility={parentGrantableAssetsVisibility.canGrantAssetsVisibility}
                    limitedByParent={!isSuperAdmin && !parentGrantableAssetsVisibility.canGrantAssetsVisibility}
                    onSave={saveCompanyAssetsVisibilityAcl}
                  />

                  <AgentLeadsCustomAclModal
                    show={showAgentLeadsCustomModal}
                    onHide={() => setShowAgentLeadsCustomModal(false)}
                    initialOptions={selectedModules[AGENT_LEADS_ACL_MODULE]?.edit_options}
                    optionsConfigured={areAclEditOptionsConfigured(selectedModules[AGENT_LEADS_ACL_MODULE])}
                    canGrantCustomLeads={parentGrantableCustomLeads.canGrantCustomLeads}
                    limitedByParent={!isSuperAdmin && !parentGrantableCustomLeads.canGrantCustomLeads}
                    onSave={saveAgentLeadsCustomAcl}
                  />

                  <UserEmailVerificationAclModal
                    show={showUsersEmailVerificationModal}
                    onHide={() => setShowUsersEmailVerificationModal(false)}
                    initialOptions={selectedModules[USERS_ACL_MODULE]?.edit_options}
                    optionsConfigured={areAclEditOptionsConfigured(selectedModules[USERS_ACL_MODULE])}
                    canGrantEmailVerification={parentGrantableUsersEmailVerification.canGrantEmailVerification}
                    limitedByParent={
                      !isSuperAdmin && !parentGrantableUsersEmailVerification.canGrantEmailVerification
                    }
                    onSave={saveUsersEmailVerificationAcl}
                  />

                  <DispositionFormsTimingAclModal
                    show={showDispositionFormsTimingModal}
                    onHide={() => setShowDispositionFormsTimingModal(false)}
                    initialOptions={selectedModules[DISPOSITION_FORMS_ACL_MODULE]?.edit_options || []}
                    optionsConfigured={areAclEditOptionsConfigured(selectedModules[DISPOSITION_FORMS_ACL_MODULE])}
                    canGrantCustomTiming={canGrantDispositionCustomTiming}
                    canGrantFollowUps={canGrantDispositionFollowUps}
                    limitedByParent={
                      !isSuperAdmin && !canGrantDispositionCustomTiming && !canGrantDispositionFollowUps
                    }
                    onSave={saveDispositionTimingOptions}
                  />

                  <TtsOperatorAclModal
                    show={showTtsOperatorModal}
                    onHide={() => setShowTtsOperatorModal(false)}
                    initialOperator={selectedModules[TTS_ACL_MODULE]?.tts_operator}
                    initialLanguages={selectedModules[TTS_ACL_MODULE]?.allowed_languages || []}
                    onSave={saveTtsOperator}
                  />

                  <AiAgentDescLimitAclModal
                    show={showAiAgentDescLimitModal}
                    onHide={() => setShowAiAgentDescLimitModal(false)}
                    initialLimit={selectedModules[AI_AGENT_ACL_MODULE]?.description_char_limit}
                    onSave={saveAiAgentDescLimit}
                  />

                  <IvrEditAclModal
                    show={showIvrModal}
                    onHide={() => setShowIvrModal(false)}
                    initialOptions={selectedModules[IVR_ACL_MODULE]?.edit_options || []}
                    optionDefs={ivrNodeOptionDefs}
                    limitedByParent={!isSuperAdmin && parentGrantableIvrOptions.length < ivrNodeOptionDefs.length}
                    onSave={saveIvrNodeOptions}
                  />

                  <VoiceLibraryAclModal
                    show={showVoiceLibraryModal}
                    onHide={() => setShowVoiceLibraryModal(false)}
                    initialValues={selectedModules[VOICE_LIBRARY_ACL_MODULE] || {}}
                    voiceTypeOptions={voiceTypeOptionsForEditor}
                    languageOptions={languageOptionsForEditor}
                    limitedByParent={!isSuperAdmin && !!parentGrantableVoiceLibrary}
                    onSave={saveVoiceLibraryAcl}
                  />

                  <div className="acl-submit-bar">
                    <Button
                      onClick={() => submitForm()}
                      type="button"
                      className="acl-submit-btn"
                      disabled={buttonDisable}
                    >
                      {buttonDisable ? (
                        <>
                          Updating…{' '}
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        </>
                      ) : (
                        'Update ACL'
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </ShadcnCard>

          <FreezePasswordPrompt
            open={showFreezePrompt}
            onOpenChange={setShowFreezePrompt}
            aclName={aclName}
            actionLabel="Save changes"
            onConfirm={(password: string) => submitForm(password)}
          />
        </div>
      </div>
    </div>
  );
};

export default ACLSettingEdit;
